/**
 * 헤더 알림 드롭다운
 * - <details> 열 때 활성 그룹의 최근 알림을 fetch → 탭/아이템 렌더
 * - 그룹 ≥ 2 일 때만 탭 row 노출
 * - 각 아이템 우측 체크 버튼 = 개별 읽음 처리 AJAX 폼 (AdminForm 패턴)
 * - 푸터 '모두 읽음' = 정적 SSR 폼 (header.php 에서 출력)
 * - 응답에 mark_all_url, mark_read_url 포함, 탭 클릭 시 fresh fetch
 */

import { bindAdminForms } from "./AdminForm";

const ROOT = "[data-admin-notifications]";

interface NotifItem {
  id: number;
  title: string;
  message: string;
  type: string;
  type_label: string;
  group_key: string;
  group_label: string;
  icon: string;
  is_read: boolean;
  action_url: string | null;
  created_at_short: string;
  mark_read_url: string;
}

interface GroupMeta {
  key: string;
  label: string;
  unread_count: number;
}

interface RecentResponse {
  success?: boolean;
  data?: {
    items?: NotifItem[];
    url?: string;
    mark_all_url?: string;
    groups?: GroupMeta[];
    unread_counts?: Record<string, number>;
    total_unread?: number;
  };
}

function getCsrfToken(root: HTMLElement): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content?.trim();
  if (meta) return meta;
  return root.dataset.csrfToken?.trim() ?? "";
}

export function initAdminNotificationsDropdown(): void {
  const root = document.querySelector<HTMLDetailsElement>(ROOT);
  if (!root) return;

  const baseUrl = root.getAttribute("data-admin-notifications-url");
  const markReadTemplate = root.getAttribute("data-admin-notifications-mark-read-url-template") ?? "";
  const listEl = root.querySelector<HTMLElement>("[data-admin-notifications-list]");
  const tabsEl = root.querySelector<HTMLElement>("[data-admin-notifications-tabs]");
  const totalPillEl = root.querySelector<HTMLElement>("[data-admin-notifications-total-pill]");
  if (!baseUrl || !listEl || !tabsEl) return;

  let activeGroup = "all";
  let groupsCache: GroupMeta[] = [];

  function renderTabs(counts: Record<string, number>): void {
    if (!tabsEl) return;
    const groupCount = groupsCache.length;
    // 그룹 ≥ 2 일 때만 탭 row 노출 (그룹 1개면 '전체' 와 동일)
    if (groupCount < 2) {
      tabsEl.hidden = true;
      tabsEl.innerHTML = "";
      return;
    }
    tabsEl.hidden = false;
    tabsEl.innerHTML = "";

    const makeTab = (key: string, label: string, count: number): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className =
        "admin-header__notifications-tab" +
        (key === activeGroup ? " admin-header__notifications-tab--active" : "");
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", key === activeGroup ? "true" : "false");
      btn.dataset.panelTab = key;
      btn.textContent = label;
      if (count > 0) {
        const badge = document.createElement("span");
        badge.className = "admin-header__notifications-tab-badge";
        badge.textContent = count > 99 ? "99+" : String(count);
        btn.appendChild(document.createTextNode(" "));
        btn.appendChild(badge);
      }
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        if (activeGroup === key) return;
        activeGroup = key;
        void fetchGroup(activeGroup);
      });
      return btn;
    };

    tabsEl.appendChild(makeTab("all", "전체", counts.all ?? 0));
    groupsCache.forEach((g) => {
      tabsEl.appendChild(makeTab(g.key, g.label, counts[g.key] ?? 0));
    });
  }

  function buildItem(it: NotifItem, csrf: string): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className =
      "admin-header__notifications-item" + (it.is_read ? "" : " is-unread");
    wrap.dataset.notificationId = String(it.id);

    const link = document.createElement("a");
    link.href = (it.action_url && it.action_url.trim() !== "") ? it.action_url : "#";
    link.className = "admin-header__notifications-item-link";

    const iconWrap = document.createElement("span");
    iconWrap.className = "admin-header__notifications-item-icon";
    iconWrap.setAttribute("aria-hidden", "true");
    const iconEl = document.createElement("i");
    iconEl.className = it.icon || "fa-light fa-bell";
    iconWrap.appendChild(iconEl);
    link.appendChild(iconWrap);

    const body = document.createElement("span");
    body.className = "admin-header__notifications-item-body";

    const title = document.createElement("span");
    title.className = "admin-header__notifications-item-title";
    title.textContent = it.title || "";
    body.appendChild(title);

    if (it.message && it.message.trim() !== "") {
      const msg = document.createElement("span");
      msg.className = "admin-header__notifications-item-msg";
      msg.textContent = it.message;
      body.appendChild(msg);
    }

    const meta = document.createElement("span");
    meta.className = "admin-header__notifications-item-meta";
    if (it.group_label) {
      const chip = document.createElement("span");
      chip.className = "admin-header__notifications-item-group-chip";
      chip.textContent = it.group_label;
      meta.appendChild(chip);
    }
    const time = document.createElement("span");
    time.className = "admin-header__notifications-item-time";
    time.textContent = it.created_at_short || "";
    meta.appendChild(time);
    body.appendChild(meta);

    link.appendChild(body);
    wrap.appendChild(link);

    if (!it.is_read) {
      const formAction =
        it.mark_read_url ||
        markReadTemplate.replace("__ID__", String(it.id));

      const form = document.createElement("form");
      form.method = "post";
      form.action = formAction;
      form.className = "admin-header__notifications-item-mark";
      form.dataset.adminAjax = "1";
      form.dataset.successAction = "notification-mark-read";

      if (csrf) {
        const csrfInput = document.createElement("input");
        csrfInput.type = "hidden";
        csrfInput.name = "_token";
        csrfInput.value = csrf;
        form.appendChild(csrfInput);
      }

      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = "PATCH";
      form.appendChild(methodInput);

      const btn = document.createElement("button");
      btn.type = "submit";
      btn.setAttribute("aria-label", "읽음 처리");
      btn.title = "읽음으로 표시";
      btn.innerHTML = `<i class="fa-light fa-check" aria-hidden="true"></i>`;
      form.appendChild(btn);

      wrap.appendChild(form);
    }

    return wrap;
  }

  function renderItems(items: NotifItem[]): void {
    if (!listEl) return;
    listEl.innerHTML = "";

    if (items.length === 0) {
      const p = document.createElement("p");
      p.className =
        "admin-header__notifications-loading admin-header__notifications-item--empty";
      p.textContent = "알림이 없습니다.";
      listEl.appendChild(p);
      return;
    }

    const csrf = getCsrfToken(root!);
    items.forEach((it) => listEl.appendChild(buildItem(it, csrf)));
    // 새로 생성된 폼들에 AdminForm 바인딩
    bindAdminForms(listEl);
  }

  function applyTotalPill(total: number): void {
    if (!totalPillEl) return;
    const n = Math.max(0, Math.floor(total));
    totalPillEl.textContent = n > 99 ? "99+" : String(n);
    totalPillEl.classList.toggle("is-unread", n > 0);
  }

  async function fetchGroup(group: string): Promise<void> {
    if (!listEl) return;
    listEl.innerHTML = '<p class="admin-header__notifications-loading">불러오는 중…</p>';
    try {
      const url = new URL(baseUrl!, window.location.origin);
      if (group !== "all") url.searchParams.set("group", group);
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });
      const json: RecentResponse = await res.json();
      const data = json?.data ?? {};
      groupsCache = Array.isArray(data.groups) ? data.groups : [];
      const counts = data.unread_counts ?? {};
      renderTabs(counts);
      renderItems(Array.isArray(data.items) ? data.items : []);
      applyTotalPill(typeof data.total_unread === "number" ? data.total_unread : (counts.all ?? 0));
    } catch {
      listEl.innerHTML =
        '<p class="admin-header__notifications-loading">불러오기에 실패했습니다.</p>';
    }
  }

  root.addEventListener("toggle", () => {
    if (root.open) {
      void fetchGroup(activeGroup);
    }
  });

  // unread 카운트 변경 이벤트 listen (AdminForm 의 mark-read/mark-all-read 후 발행)
  window.addEventListener("admin:notifications:unread-changed", (e: Event) => {
    const detail = (e as CustomEvent).detail as
      | { unread?: number; unread_counts?: Record<string, number> }
      | undefined;
    if (!detail) return;
    const counts = detail.unread_counts ?? {};
    if (!tabsEl.hidden) {
      renderTabs(counts);
    }
    applyTotalPill(typeof detail.unread === "number" ? detail.unread : (counts.all ?? 0));
  });
}
